import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import {
  Check,
  X,
  Eye,
  Clock,
  ChevronDown,
  ChevronUp,
  FileText,
} from "lucide-react";
import {
  useApprovalConfig,
  usePendingBatches,
  useBatchDetail,
  useApproveBatch,
  useRejectBatch,
  useReportHtml,
} from "../../hooks/admin/use-admin-approvals";
const SKIP_REASON_LABELS: Record<string, string> = {
  inactive_or_suspended: "Contact inactive or suspended",
  complained: "Spam complaint received",
  duplicate_prevention: "Report already sent recently",
  no_cached_data: "Suburb data unavailable",
  no_data: "No market data found",
  validation_failed: "Report validation failed",
};
import { HorizontalScrollTable } from "@/components/molecules/HorizontalScrollTable";
import { useResponsiveDialog } from "@/hooks/useResponsiveDialog";

export function AdminApprovals() {
  const { data: config, isLoading: configLoading } = useApprovalConfig();
  const { data: batchesData, isLoading: batchesLoading } = usePendingBatches();
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [previewReportId, setPreviewReportId] = useState<string | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [actionBatchId, setActionBatchId] = useState<string | null>(null);

  const { data: batchDetail, isLoading: detailLoading } =
    useBatchDetail(expandedBatchId);
  const approveMutation = useApproveBatch();
  const rejectMutation = useRejectBatch();
  const { data: previewHtml } = useReportHtml(expandedBatchId, previewReportId);
  const { fullScreen, TransitionComponent } = useResponsiveDialog();

  const handleExpand = (batchId: string) => {
    setExpandedBatchId(expandedBatchId === batchId ? null : batchId);
  };

  const handleApproveClick = (batchId: string) => {
    setActionBatchId(batchId);
    setConfirmApproveOpen(true);
  };

  const handleRejectClick = (batchId: string) => {
    setActionBatchId(batchId);
    setRejectDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!actionBatchId) return;
    try {
      await approveMutation.mutateAsync(actionBatchId);
      setConfirmApproveOpen(false);
      setActionBatchId(null);
      setExpandedBatchId(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleReject = async () => {
    if (!actionBatchId || !rejectReason.trim()) return;
    try {
      await rejectMutation.mutateAsync({
        batchId: actionBatchId,
        reason: rejectReason,
      });
      setRejectDialogOpen(false);
      setRejectReason("");
      setActionBatchId(null);
      setExpandedBatchId(null);
    } catch {
      // Error handled by mutation
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getTimeRemaining = React.useCallback((autoApproveAt: string | null) => {
    if (!autoApproveAt) return null;
    const now = Date.now();
    const remaining = new Date(autoApproveAt).getTime() - now;
    if (remaining <= 0) return "Auto-approving soon...";
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  }, []);

  const currentBatch = batchesData?.batches.find(
    (b) => b.id === expandedBatchId,
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Report Approvals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve report batches before sending
          </Typography>
        </Box>
      </Box>

      {/* Config Status */}
      {configLoading ? (
        <Skeleton variant="rounded" height={48} sx={{ mb: 3 }} />
      ) : config ? (
        <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Chip
              label={
                config.approvalEnabled
                  ? "Approval Required"
                  : "Approval Disabled"
              }
              color={config.approvalEnabled ? "warning" : "default"}
              size="small"
              variant="outlined"
            />
            <Chip
              label={
                config.autoApprovalEnabled
                  ? `Auto-approve: ${config.autoApprovalDelayHours}h`
                  : "Auto-approve: Off"
              }
              color={config.autoApprovalEnabled ? "info" : "default"}
              size="small"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              {config.superAdminEmailCount} super-admin(s) configured for
              notifications
            </Typography>
          </Box>
        </Card>
      ) : null}

      {/* Error alerts */}
      {approveMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {approveMutation.error?.message || "Failed to approve batch"}
        </Alert>
      )}
      {rejectMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {rejectMutation.error?.message || "Failed to reject batch"}
        </Alert>
      )}

      {/* Pending Batches */}
      <Typography
        variant="h6"
        fontWeight={600}
        color="text.primary"
        sx={{ mb: 2 }}
      >
        Pending Batches
      </Typography>

      {batchesLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={64} />
          ))}
        </Box>
      ) : batchesData?.batches.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No batches pending approval
          </Typography>
        </Card>
      ) : (
        <Card variant="outlined" sx={{ overflow: "hidden" }}>
          <HorizontalScrollTable minWidth={800}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Reports</TableCell>
                  <TableCell align="center">Total Sales</TableCell>
                  <TableCell align="center">Total Listings</TableCell>
                  <TableCell>Auto-approve</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchesData?.batches.map((batch) => (
                  <>
                    <TableRow key={batch.id} hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleExpand(batch.id)}
                        >
                          {expandedBatchId === batch.id ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(batch.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={batch.reportCount - (batch.skippedCount ?? 0)}
                          size="small"
                          variant="outlined"
                        />
                        {(batch.skippedCount ?? 0) > 0 && (
                          <Chip
                            label={`${batch.skippedCount} skipped`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ ml: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {batch.totalSales}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {batch.totalListings}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {batch.autoApproveAt ? (
                          <Tooltip
                            title={`Auto-approve at ${formatDate(batch.autoApproveAt)}`}
                          >
                            <Chip
                              icon={<Clock size={14} />}
                              label={getTimeRemaining(batch.autoApproveAt)}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            label="Manual only"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            onClick={() => handleExpand(batch.id)}
                          >
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApproveClick(batch.id)}
                            disabled={approveMutation.isPending}
                          >
                            <Check size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectClick(batch.id)}
                            disabled={rejectMutation.isPending}
                          >
                            <X size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Detail Row */}
                    <TableRow key={`${batch.id}-detail`}>
                      <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                        <Collapse
                          in={expandedBatchId === batch.id}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ p: 2, bgcolor: "grey.50" }}>
                            {detailLoading ? (
                              <Skeleton variant="rounded" height={120} />
                            ) : batchDetail?.batch ? (
                              <Box>
                                {(batchDetail.batch.skippedDetails?.length ??
                                  0) > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Alert severity="warning" sx={{ mb: 1 }}>
                                      {batchDetail.batch.skippedCount} contact
                                      {batchDetail.batch.skippedCount === 1
                                        ? " was"
                                        : "s were"}{" "}
                                      skipped during report generation
                                    </Alert>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Contact</TableCell>
                                          <TableCell>Email</TableCell>
                                          <TableCell>Suburb</TableCell>
                                          <TableCell>Reason</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {batchDetail.batch.skippedDetails.map(
                                          (skip) => (
                                            <TableRow key={skip.contactId}>
                                              <TableCell>
                                                <Typography
                                                  variant="body2"
                                                  fontWeight={500}
                                                >
                                                  {skip.contactName}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                >
                                                  {skip.contactEmail}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                <Typography variant="body2">
                                                  {skip.suburb}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={
                                                    SKIP_REASON_LABELS[
                                                      skip.reason
                                                    ] ?? skip.reason
                                                  }
                                                  size="small"
                                                  color="warning"
                                                  variant="outlined"
                                                />
                                              </TableCell>
                                            </TableRow>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                )}

                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                  sx={{ mb: 2 }}
                                >
                                  Reports in this batch
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Contact</TableCell>
                                      <TableCell>Email</TableCell>
                                      <TableCell>Suburb</TableCell>
                                      <TableCell align="center">
                                        Sales
                                      </TableCell>
                                      <TableCell align="center">
                                        Listings
                                      </TableCell>
                                      <TableCell align="right">
                                        Preview
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {batchDetail.batch.reports?.map(
                                      (report) => (
                                        <TableRow key={report.id}>
                                          <TableCell>
                                            <Typography
                                              variant="body2"
                                              fontWeight={500}
                                            >
                                              {report.contactName || "Unknown"}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Box>
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                              >
                                                {report.emailRecipients?.[0] ||
                                                  report.contactEmail ||
                                                  "-"}
                                              </Typography>
                                              {report.emailRecipients &&
                                                report.emailRecipients.length >
                                                  1 && (
                                                  <Typography
                                                    variant="caption"
                                                    color="primary"
                                                  >
                                                    +
                                                    {report.emailRecipients
                                                      .length - 1}{" "}
                                                    more
                                                  </Typography>
                                                )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {report.suburb || "-"}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">
                                            {report.salesCount ?? 0}
                                          </TableCell>
                                          <TableCell align="center">
                                            {report.listingsCount ?? 0}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Tooltip title="Preview email">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  setPreviewReportId(report.id)
                                                }
                                              >
                                                <FileText size={16} />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      ),
                                    )}
                                  </TableBody>
                                </Table>

                                <Box
                                  sx={{
                                    mt: 2,
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<Check size={16} />}
                                    onClick={() => handleApproveClick(batch.id)}
                                    disabled={approveMutation.isPending}
                                  >
                                    Approve All (
                                    {batchDetail.batch.reportCount -
                                      (batchDetail.batch.skippedCount ?? 0)}
                                    )
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<X size={16} />}
                                    onClick={() => handleRejectClick(batch.id)}
                                    disabled={rejectMutation.isPending}
                                  >
                                    Reject
                                  </Button>
                                </Box>
                              </Box>
                            ) : null}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                ))}
              </TableBody>
            </Table>
          </HorizontalScrollTable>
        </Card>
      )}

      {/* Approve Confirmation Dialog */}
      <Dialog
        open={confirmApproveOpen}
        onClose={() => setConfirmApproveOpen(false)}
        fullScreen={fullScreen}
        TransitionComponent={TransitionComponent}
      >
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          {(() => {
            const reports = batchDetail?.batch?.reports ?? [];
            const totalEmails = reports.reduce((sum, r) => {
              const recipientCount = r.emailRecipients?.length || 1;
              return sum + recipientCount;
            }, 0);
            const reportCount =
              currentBatch?.reportCount || batchDetail?.batch.reportCount || 0;
            const skipped =
              currentBatch?.skippedCount ??
              batchDetail?.batch.skippedCount ??
              0;
            const actualReports = reportCount - skipped;
            return (
              <>
                <Typography>
                  Are you sure you want to approve this batch?{" "}
                  {totalEmails > actualReports
                    ? `${totalEmails} emails (${actualReports} reports) will be queued for sending.`
                    : `${actualReports} emails will be queued for sending.`}
                </Typography>
                {skipped > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {skipped} contact{skipped === 1 ? " was" : "s were"} skipped
                    during generation and will not receive emails.
                  </Alert>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApproveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={approveMutation.isPending}
          >
            {approveMutation.isPending ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        TransitionComponent={TransitionComponent}
      >
        <DialogTitle>Reject Batch</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please provide a reason for rejection. This will be logged for audit
            purposes.
          </Typography>
          <TextField
            autoFocus
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejectMutation.isPending || !rejectReason.trim()}
          >
            {rejectMutation.isPending ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* HTML Preview Dialog */}
      <Dialog
        open={!!previewReportId}
        onClose={() => setPreviewReportId(null)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        TransitionComponent={TransitionComponent}
      >
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent sx={{ p: 0, height: fullScreen ? "100%" : 600 }}>
          {previewHtml && (
            <iframe
              srcDoc={previewHtml}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Email Preview"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewReportId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
